import { Children, type ReactNode, isValidElement } from "react";

type Resolvable<T = void> = ReactNode | ((value: T) => ReactNode);

// biome-ignore lint/suspicious/noExplicitAny: internal helper, external API is fully typed
function resolve(children: Resolvable<any>, value?: unknown): ReactNode {
  if (typeof children === "function") {
    return children(value);
  }
  return children;
}

// ---------------------------------------------------------------------------
// If / ElseIf / Else
// ---------------------------------------------------------------------------

type Falsy = false | 0 | "" | null | undefined;

type IfProps<T = boolean> =
  | { condition: boolean; when?: never; children: Resolvable }
  | { condition?: never; when: T | Falsy; children: Resolvable<T & {}> };

interface ElseIfProps {
  condition: boolean;
  children: Resolvable;
}

interface ElseProps {
  children: Resolvable;
}

interface Branch {
  condition: boolean;
  children: Resolvable;
}

function categorizeBranches(ifChildren: Resolvable, rawChildren: ReactNode) {
  const elseIfs: Branch[] = [];
  let elseChildren: Resolvable | null = null;
  const ifContent: ReactNode[] = [];

  Children.forEach(rawChildren, (child) => {
    if (!isValidElement(child)) {
      ifContent.push(child);
      return;
    }

    if (child.type === ElseIf) {
      const props = child.props as ElseIfProps;
      elseIfs.push({ condition: props.condition, children: props.children });
    } else if (child.type === Else) {
      if (process.env.NODE_ENV !== "production" && elseChildren !== null) {
        console.warn("[Conditional] <If> should contain at most one <Else>.");
      }
      elseChildren = (child.props as ElseProps).children;
    } else {
      ifContent.push(child);
    }
  });

  const resolvedIfChildren =
    ifContent.length > 0 ? <>{ifContent}</> : ifChildren;

  return { ifChildren: resolvedIfChildren, elseIfs, elseChildren };
}

function If<T>(props: IfProps<T>): ReactNode {
  const isWhenMode = "when" in props && props.condition === undefined;
  const isTruthy = isWhenMode ? !!props.when : props.condition;
  const narrowedValue = isWhenMode ? (props.when as T & {}) : undefined;
  const { children } = props;

  const flat = Children.toArray(
    typeof children === "function" ? null : (children as ReactNode),
  );

  const hasControlFlow = flat.some(
    (child) =>
      isValidElement(child) &&
      (child.type === ElseIf || child.type === Else),
  );

  if (!hasControlFlow) {
    return isTruthy ? resolve(children, narrowedValue) : null;
  }

  // biome-ignore lint/suspicious/noExplicitAny: children type varies between condition/when modes
  const branches = categorizeBranches(children as Resolvable<any>, children as ReactNode);

  if (isTruthy) {
    return resolve(branches.ifChildren, narrowedValue);
  }

  for (const branch of branches.elseIfs) {
    if (branch.condition) {
      return resolve(branch.children);
    }
  }

  if (branches.elseChildren !== null) {
    return resolve(branches.elseChildren);
  }

  return null;
}

function ElseIf(_props: ElseIfProps): ReactNode {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "[Conditional] <ElseIf> must be a direct child of <If>. Rendering nothing.",
    );
  }
  return null;
}

function Else(_props: ElseProps): ReactNode {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "[Conditional] <Else> must be a direct child of <If>. Rendering nothing.",
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Switch / Case / Default
// ---------------------------------------------------------------------------

interface SwitchProps<T> {
  on: T;
  children: ReactNode;
}

interface CaseProps<T> {
  value?: T;
  when?: (value: T) => boolean;
  children: Resolvable;
}

interface DefaultProps {
  children: Resolvable;
}

function Switch<T>({ on, children }: SwitchProps<T>): ReactNode {
  let defaultChildren: Resolvable | null = null;

  const cases: ReactNode[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }

    if (child.type === Default) {
      if (process.env.NODE_ENV !== "production" && defaultChildren !== null) {
        console.warn(
          "[Conditional] <Switch> should contain at most one <Default>.",
        );
      }
      defaultChildren = (child.props as DefaultProps).children;
      return;
    }

    if (child.type === Case) {
      cases.push(child);
      return;
    }

    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "[Conditional] <Switch> children should be <Case> or <Default>.",
      );
    }
  });

  for (const caseElement of cases) {
    if (!isValidElement(caseElement)) {
      continue;
    }

    const props = caseElement.props as CaseProps<T>;

    if (process.env.NODE_ENV !== "production") {
      if (props.value === undefined && props.when === undefined) {
        console.warn(
          "[Conditional] <Case> requires either a `value` or `when` prop.",
        );
      }
      if (props.value !== undefined && props.when !== undefined) {
        console.warn(
          "[Conditional] <Case> should have either `value` or `when`, not both.",
        );
      }
    }

    if (props.when !== undefined) {
      if (props.when(on)) {
        return resolve(props.children);
      }
      continue;
    }

    if (props.value !== undefined && props.value === on) {
      return resolve(props.children);
    }
  }

  if (defaultChildren !== null) {
    return resolve(defaultChildren);
  }

  return null;
}

function Case<T>(_props: CaseProps<T>): ReactNode {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "[Conditional] <Case> must be a direct child of <Switch>. Rendering nothing.",
    );
  }
  return null;
}

function Default(_props: DefaultProps): ReactNode {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "[Conditional] <Default> must be a direct child of <Switch>. Rendering nothing.",
    );
  }
  return null;
}

export { If, ElseIf, Else, Switch, Case, Default };
